"use client"

import { cn } from "@/lib/utils"

/**
 * Secondary "Installments" badge rendered alongside the invoice
 * ``StatusBadge`` when the invoice carries an installment plan. The
 * status itself stays the existing status badge; this badge only flags
 * that the invoice is billed as an installment plan (sibling of
 * ``DepositBadge``). Purple ``bg-primary/10 text-primary`` so it reads
 * distinctly from the blue deposit badge at a glance.
 *
 * With a ``count`` it also carries the shape of the plan — "Installments ·
 * 3" — and once a payment has settled, how much is left to collect:
 * "Installments · 3 · 1 left" (BILL-238 / PM decision D3). The numbers
 * live here rather than in a table column of their own because this badge
 * is already rendered exactly for installment invoices and exactly nowhere
 * else, so a column would be empty on the large majority of rows.
 *
 * Both props are optional and default to the bare "Installments" text, so
 * the detail header and every hand-built fixture render today's bytes
 * unchanged.
 */
export function InstallmentBadge({
  count,
  remaining,
  className,
  testId = "installment-badge",
}: {
  /** Total payments on the plan. `null`/absent renders the bare badge. */
  count?: number | null
  /**
   * Payments still to collect. Shown only once it is *below* the count —
   * a plan nobody has paid yet says "3", not "3 · 3 left". Compared
   * numerically rather than for truthiness: `0` left is the whole point of
   * the final render ("3 · 0 left" on a settled plan).
   */
  remaining?: number | null
  className?: string
  testId?: string
}) {
  const parts = ["Installments"]
  if (count !== null && count !== undefined) {
    parts.push(String(count))
    if (remaining !== null && remaining !== undefined && remaining < count) {
      parts.push(`${remaining} left`)
    }
  }
  return (
    <span
      data-testid={testId}
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded text-caption font-medium bg-primary/10 text-primary",
        className,
      )}
    >
      {parts.join(" · ")}
    </span>
  )
}
